import fetchAPI from '../../../utils/fetchApi';
import { getSessionStorageItem, setSessionStorageItem } from '../../../utils/sStorage';
import formatFeedItemsUrl from './utils/formatFeedItemsUrl';
import generateFeedItemsParams from './utils/generateFeedItemsParams';
import createFavorite from '../../../repositories/createFavorite';
import { trackGAEvent } from '../../../utils/gaTracking';
import wasPageReloaded from '../../../utils/wasPageReloaded';

import { TFeed, TFeedItem } from 'types/TFeeds';

type TCachedFeed = { feedItemsPage: number; feedItems: TFeedItem[] };

const isValidFeedItem = (item: unknown): item is TFeedItem =>
  item !== null &&
  typeof item === 'object' &&
  typeof (item as Record<string, unknown>).itemId === 'string' &&
  (item as Record<string, unknown>).itemId !== '' &&
  typeof (item as Record<string, unknown>).url === 'string';

const readCache = (key: string, ttl?: number): TCachedFeed | null => {
  const raw = getSessionStorageItem(key, ttl);
  if (raw === null || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (typeof d.feedItemsPage !== 'number') return null;
  if (!Array.isArray(d.feedItems)) return null;
  const validItems = (d.feedItems as unknown[]).filter(isValidFeedItem);
  if (validItems.length === 0) return null;
  return { feedItemsPage: d.feedItemsPage, feedItems: validItems };
};

const L1PAGE_FEED = 'L1PAGE_FEED';

type TGetFeedItemsProps = {
  l1CategoryId: number;
  feed: TFeed;
  page: number;
  feedPostcode?: string;
  userLoggedIn?: boolean;
  xsrfToken?: string;
  favoriteAdIds?: string[];
};

type TGetCurrentPageProps = {
  l1CategoryId: number;
  feed: TFeed;
  feedPostcode?: string;
};

type TGetFeedItemsResult = {
  items: TFeedItem[];
  autoFavoriteStatus?: 'success' | 'error';
};

const minutes = 60;
const seconds = 60;
const milliseconds = 1000;
const ttlInHours = 6;
// convert hours to milliseconds
const cacheTtl = ttlInHours * minutes * seconds * milliseconds;

const formatCacheKey = ({ categoryId, feedType, postcode }): string =>
  postcode ? `${L1PAGE_FEED}_${categoryId}_${feedType}_${postcode}` : `${L1PAGE_FEED}_${categoryId}_${feedType}`;

async function checkIfFavoriteItem(feedItems: TFeedItem[], xsrfToken: string): Promise<TGetFeedItemsResult> {
  const favItem = getSessionStorageItem('FAVORITE_ITEM');
  if (!favItem) return { items: feedItems };

  trackGAEvent('WatchlistAddBegin');

  sessionStorage.removeItem('FAVORITE_ITEM_CREATED');
  sessionStorage.removeItem('FAVORITE_ITEM_VALUE');

  try {
    const response = await createFavorite(favItem.itemId, xsrfToken);
    if (!response?.isFavorite) {
      return { items: feedItems, autoFavoriteStatus: 'error' };
    }
    trackGAEvent('WatchlistAddSuccess');
    return {
      items: feedItems.map((f) => (f.itemId === favItem.itemId ? { ...f, isFavorite: true } : f)),
      autoFavoriteStatus: 'success',
    };
  } catch {
    return { items: feedItems, autoFavoriteStatus: 'error' };
  }
}

export default async function getFeedItems({
  l1CategoryId,
  feed,
  feedPostcode,
  page,
  userLoggedIn,
  xsrfToken,
  favoriteAdIds,
}: TGetFeedItemsProps): Promise<TGetFeedItemsResult> {
  const params = generateFeedItemsParams({ l1CategoryId, feed, feedPostcode, page });

  const storageKey = formatCacheKey(params);

  // try to return cached items first
  // if the number of the cached page is greater or equal to the incoming then we return data for all cached pages
  const cachedData = readCache(storageKey, cacheTtl);
  if (cachedData !== null && cachedData.feedItemsPage >= page) {
    if (wasPageReloaded() && userLoggedIn) {
      const favoriteIds = new Set(favoriteAdIds ?? []);
      const patchedItems = cachedData.feedItems.map((item: TFeedItem) => ({
        ...item,
        isFavorite: favoriteIds.has(item.itemId),
      }));
      setSessionStorageItem(storageKey, { ...cachedData, feedItems: patchedItems });
      if (xsrfToken && page === 0) {
        return checkIfFavoriteItem(patchedItems, xsrfToken);
      }
      return { items: patchedItems };
    }
    if (userLoggedIn && xsrfToken && page === 0) {
      return checkIfFavoriteItem(cachedData.feedItems, xsrfToken);
    }
    return { items: cachedData.feedItems };
  }

  try {
    const feedItems = await fetchAPI({ url: formatFeedItemsUrl(params), preloadCompatible: true });

    if (feedItems?.length) {
      // cache response only if it contains feed items
      setSessionStorageItem(storageKey, {
        feedItemsPage: page,
        feedItems: [...(cachedData?.feedItems || []), ...feedItems],
      });
    }

    if (userLoggedIn && xsrfToken && page === 0) {
      return checkIfFavoriteItem(feedItems ?? [], xsrfToken);
    }

    return { items: feedItems };
  } catch {
    return { items: [] };
  }
}

export function getCurrentPage({ l1CategoryId, feed, feedPostcode }: TGetCurrentPageProps) {
  const storageKey = formatCacheKey(generateFeedItemsParams({ l1CategoryId, feed, feedPostcode }));
  const cachedData = readCache(storageKey, cacheTtl);
  return cachedData?.feedItemsPage ?? 0;
}

export function updateFeedItemFavorite(
  itemId: string,
  isFavorite: boolean,
  { l1CategoryId, feed, feedPostcode }: TGetCurrentPageProps,
) {
  const storageKey = formatCacheKey(generateFeedItemsParams({ l1CategoryId, feed, feedPostcode }));
  const cachedData = readCache(storageKey);

  if (cachedData?.feedItems) {
    setSessionStorageItem(storageKey, {
      feedItemsPage: cachedData.feedItemsPage,
      feedItems: cachedData.feedItems.map((f: TFeedItem) => (f.itemId === itemId ? { ...f, isFavorite } : f)),
    });
  }
}
